import { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { encrypt } from "Utilities/decryption";
import {
    ACCESS_TOKEN,
    ACCESS_TOKEN_API,
    RESET_PASSWORD_REQUEST,
    UPDATE_PASSWORD,
    IDENTITY_EXHCANGE_API
} from "../../constants";
import { LoginInputs } from "./helpers";
import { getURLParam } from "Utilities/generalUtilities";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "reducers";
import { useTranslation } from "react-i18next";
import { showToast } from "components/DesignSystem/Toast";
import { inputTypes } from "components/DesignSystem/Input/helpers";
import { SelectValue } from "interfaces/Select/Value";
import { LanguageInterface } from "actions/MultiLingual/types";
import { getLanguageOptions, setCurrentLanguage } from "actions";
import { MixPanel } from "MixPanel";
import { ReactGa } from "GoogleAnalytics";

type AuthState = {
    error?: boolean;
    message?: string;
    isLoggingIn?: boolean;
    isEmailSent?: boolean;
    toggleLoginError?: boolean;
    changeErrorMessage?: string;
    type?: inputTypes;
    isLoading?: boolean;
    isPasswordUpdated?: boolean;
    resetPasswordResponse?: string;
};

type forgetPassword = {
    email: string;
};

export interface resetPasswordInterFace {
    password: string;
    uid?: string;
    token: string;
    logId: string;
    confirmPassword?: string;
}
type ValidationResponse = {
    internal: boolean;
    accessToken?: string;
    errorCode?: number;
    error?: string;
};

export const identityExchange = async (): Promise<ValidationResponse> => {
    try {
        const res = await axios.post(
            IDENTITY_EXHCANGE_API,
            { url: window.location.href },
            { withCredentials: true }
        );

        return {
            internal: res.data.internal === true,
            accessToken: res.data.access,
            errorCode: res.data.error_code,
            error: res.data.error
        };
    } catch (error: any) {
        const res = error.response;
        if (res?.data) {
            return {
                internal: res.data?.internal ?? false,
                errorCode: res.data?.error_code,
                error: res.data?.error
            };
        }
        return {
            internal: false,
            errorCode: 2500,
            error: error.message || "Network error"
        };
    }
};

export const useLogin = () => {
    const { t } = useTranslation();

    const [loginState, setLoginState] = useState<AuthState>({
        error: false,
        message: "",
        isLoggingIn: false,
        isEmailSent: false,
        toggleLoginError: false,
        type: "password",
        isLoading: false,
        isPasswordUpdated: false
    });

    const currentCity = useSelector((state: RootState) => state.city.currentCity);

    const handleClick = () => {
        setLoginState((prev) => ({
            ...prev,
            type: prev.type === "password" ? "text" : "password"
        }));
        if (loginState.type === "password") {
            setLoginState((prev) => ({ ...prev, type: "text" }));
        } else {
            setLoginState((prev) => ({ ...prev, type: "password" }));
        }
    };

    const loginSuccess = () => {
        setLoginState((prev) => ({ ...prev, toggleLoginError: false }));
        const nextParam = getURLParam("next");
        if (nextParam) {
            window.location.href = nextParam;
        } else {
            if (currentCity) {
                window.location.href = `/${currentCity.default_home_page}`;
            }
        }
    };

    const handleLogin = async (values: LoginInputs) => {
        try {
            setLoginState((prev) => ({ ...prev, isLoading: true }));
            const encryptedData = {
                username: encrypt(values.username),
                password: encrypt(values.password)
            };
            axios
                .post(ACCESS_TOKEN_API, encryptedData)
                .then((res) => {
                    if (!localStorage.getItem("version")) {
                        localStorage.setItem("version", res.headers["x-application-version"]);
                    }
                    if (res.status === 302 || res.status === 405 || res.status === 200) {
                        localStorage.setItem(ACCESS_TOKEN, res.data.access);
                        localStorage.setItem("isUserAuthenticated", "true");
                        localStorage.setItem("lastAPICall", JSON.stringify(Date.now()));
                        setLoginState((prev) => ({
                            ...prev,
                            toggleLoginError: false,
                            changeErrorMessage: ""
                        }));
                        loginSuccess();
                        setLoginState((prev) => ({
                            ...prev,
                            isLoading: false
                        }));
                    }
                })
                .catch((res) => {
                    if (res.response.status === 302 || res.response.status === 405) {
                        localStorage.setItem(ACCESS_TOKEN, res.data.access);
                        localStorage.setItem("isUserAuthenticated", "true");
                        setLoginState((prev) => ({
                            ...prev,
                            toggleLoginError: false,
                            changeErrorMessage: ""
                        }));

                        loginSuccess();
                    } else if (
                        res.response.status === 401 ||
                        res.response.status === 402 ||
                        res.response.status === 403
                    ) {
                        showToast(res.response.data.detail, { autoClose: 300, type: "error" });
                        setLoginState((prev) => ({
                            ...prev,
                            toggleLoginError: false,
                            changeErrorMessage: "",
                            isLoading: false
                        }));
                    } else {
                        alert(t("An error occured! Please try again"));
                    }
                })
                .finally(() => {
                    setLoginState((prev) => ({
                        ...prev,
                        setIsLoggingIn: false
                    }));
                });
        } catch (error) {
            setLoginState((prev) => ({
                ...prev,
                error: true,
                message: (error as any).response?.data?.detail || "An error occurred!",
                isLoggingIn: false
            }));
        }
    };

    const handleForgetPassword = async (data: forgetPassword) => {
        try {
            setLoginState((prev) => ({ ...prev, isLoading: true }));

            const res = await axios.post(RESET_PASSWORD_REQUEST, data);

            if (res.status === 200) {
                setLoginState((prev) => ({
                    ...prev,
                    toggleLoginError: false,
                    changeErrorMessage: "",
                    isEmailSent: true,
                    resetPasswordResponse: res.data.message
                }));
            }
        } catch (error: any) {
            showToast(error.response.data.message, { autoClose: 3000, type: "error" });
        }
        setLoginState((prev) => ({ ...prev, isLoading: false }));
    };

    const handleResetPassword = async (values: resetPasswordInterFace) => {
        try {
            setLoginState((prev) => ({
                ...prev,
                isLoading: true
            }));

            const data: any = {
                ...values,
                password: encrypt(values.password)
            };

            axios
                .post(UPDATE_PASSWORD, data)
                .then((res) => {
                    if (res.status === 200) {
                        setLoginState((prev) => ({
                            ...prev,
                            toggleLoginError: false,
                            changeErrorMessage: "",
                            isLoading: false,
                            isPasswordUpdated: true
                        }));
                    }
                })
                .catch((res) => {
                    showToast(res.response.data.error, { autoClose: 3000, type: "error" });
                    setLoginState((prev) => ({
                        ...prev,
                        isLoading: false
                    }));
                });
        } catch (error: any) {
            showToast(error.response.data.message, { autoClose: 3000, type: "error" });
        }
    };

    return {
        loginState,
        handleLogin,
        handleForgetPassword,
        handleClick,
        type: loginState.type,
        isEmailSent: loginState.isEmailSent,
        handleResetPassword,
        isLoading: loginState.isLoading,
        isPasswordUpdated: loginState.isPasswordUpdated,
        isLoggingIn: loginState.isLoggingIn,
        resetPasswordResponse: loginState.resetPasswordResponse
    };
};

const setLabel = (language: LanguageInterface): string =>
    language.display_name || language.name.toUpperCase().substring(0, 3);

export const useLanguageChange = () => {
    const dispatch = useDispatch();
    const languages = useSelector((state: RootState) => state.MultiLingual.languageOptions);
    const currentLanguage = useSelector((state: RootState) => state.MultiLingual.currentLanguage);
    const currentUser = useSelector((state: RootState) => state.user.currentUser);

    const [selectedLang, setSelectedLang] = useState<SelectValue>(currentLanguage);

    const languageOptions = useMemo(
        () =>
            languages?.results?.map((lan) => ({
                label: lan.display_name || lan.name.toUpperCase().substring(0, 3),
                value: lan.name
            })) || [],
        [languages]
    );

    useEffect(() => {
        if (!languages?.results?.length) return;

        let langToUse;
        // The below code is commented because we are not storing user's language preference.
        // if (currentUser?.language) {
        //     langToUse = languages.results.find((l) => l.id === currentUser.language.id);
        // }
        if (!langToUse) {
            langToUse = languages.results.find((l) => l.is_default === true);
        }
        if (!langToUse) {
            langToUse = languages.results[0];
        }

        if (langToUse) {
            const selected = {
                label: setLabel(langToUse),
                value: langToUse.name
            };

            setSelectedLang(selected);
            dispatch(setCurrentLanguage({ currentLanguage: selected }));
        }
    }, [languages, currentUser, dispatch]);

    useEffect(() => {
        if (languageOptions.length > 0) {
            return;
        }
        dispatch(
            getLanguageOptions({
                params: {
                    fields: "id,name,display_name,data,is_default",
                    limit: "all",
                    offset: 0,
                    ordering: "id"
                }
            })
        );
    }, [dispatch, languageOptions]);

    const handleLanguageChange = useCallback(
        (lanOpt: SelectValue) => {
            const tempSelectedLan = languages.results.find((l) => l.name === lanOpt.value);

            if (!tempSelectedLan) return;

            const tempLanguage = {
                label: setLabel(tempSelectedLan),
                value: tempSelectedLan.name
            };

            setSelectedLang(tempLanguage);
            dispatch(setCurrentLanguage({ currentLanguage: tempLanguage }));

            if (currentLanguage.value !== tempSelectedLan.name) {
                MixPanel.track("Change Language");
                ReactGa.event({
                    action: "Change Language",
                    category: "Language"
                });
            }
        },
        [languages, dispatch, currentUser, currentLanguage]
    );

    return useMemo(
        () => ({
            languageOptions,
            languages,
            handleLanguageChange,
            setLabel,
            selectedLang
        }),
        [languageOptions, languages, currentLanguage, handleLanguageChange]
    );
};
